import React, { useState, useEffect } from "react";
import axios from "axios";
import { apiurl } from "../config/config";
import { toast } from "react-toastify";

const BulkAssignManager = ({ show, onClose, refreshList }) => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState("");

  const [managerSearch, setManagerSearch] = useState(""); // used to query API
  const [managerInput, setManagerInput] = useState("");   // used to filter results
  const [managerOptions, setManagerOptions] = useState([]);
  const [selectedManager, setSelectedManager] = useState(null);

  // Fetch employees with debounce
  useEffect(() => {
    if (!show) return;
    const delayDebounce = setTimeout(() => {
      fetchEmployees();
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [employeeSearch]);

  // Reset modal state when opened
  useEffect(() => {
    if (show) {
      fetchEmployees();
      setSelectedEmployees([]);
      setSelectedManager(null);
      setManagerOptions([]);
      setManagerSearch("");
      setManagerInput("");
      setEmployeeSearch("");
    }
  }, [show]);

  // API to fetch employees
  const fetchEmployees = async () => {
    try {
      const res = await axios.get(
        `${apiurl}/api/employee/filter?search=${employeeSearch}`
      );
      setEmployees(res.data.employees || []);
    } catch (err) {
      console.error("Error fetching employees:", err);
    }
  };

  // Fetch manager list (once) by role via button
    useEffect(() => {
        handleManagerSearch();
    },[show]);
    
  const handleManagerSearch = async () => {
    try {
      const res = await axios.post(`${apiurl}/api/employee/getbyrole`, {
        designation: [
          "senior-tech-lead",
          "assistant-manager",
          "team-leader",
          "team-lead",
          "manager",
          "payroll-specialist",
          "vice-president",
          "senior-manager",
          "senior-team-lead",
          "chief-executive-officer",
          "chief-business-officer",
          "chief-data-officer",
          "sales-head",
        ],
        employeeStatus: "active",
        search: managerSearch,
      });
      setManagerOptions(res.data.users || []);
    } catch (err) {
      console.error("Manager search failed:", err);
      toast.error("Manager search failed");
    }
  };

  // Handle checkbox toggle
  const handleCheckboxChange = (emp) => {
    setSelectedEmployees((prev) =>
      prev.find((e) => e._id === emp._id)
        ? prev.filter((e) => e._id !== emp._id)
        : [...prev, emp]
    );
  };

  // Remove selected employee
  const removeEmployee = (empId) => {
    setSelectedEmployees((prev) => prev.filter((e) => e._id !== empId));
  };

  // Remove selected manager
  const removeManager = () => {
    setSelectedManager(null);
  };

  const [bulkCodeInput, setBulkCodeInput] = useState("");

// const handleBulkPaste = () => {
//   if (!bulkCodeInput.trim()) return;

//   const codes = bulkCodeInput
//     .split(",")
//     .map((c) => c.trim())
//     .filter((c) => /^[0-9]+$/.test(c)); // only numbers

//   const found = [];
//   const notFound = [];

//   codes.forEach((code) => {
//     const emp = employees.find((e) => e.employeeId === code);
//     if (emp) {
//       // Add if not already selected
//       if (!selectedEmployees.some((se) => se._id === emp._id)) {
//         found.push(emp);
//       }
//     } else {
//       notFound.push(code);
//     }
//   });

//   if (found.length > 0) {
//     setSelectedEmployees((prev) => [...prev, ...found]);
//   }

//   if (notFound.length > 0) {
//     toast.warn(`Employee codes not found: ${notFound.join(", ")}`);
//   }

//   setBulkCodeInput(""); // clear after processing
// };

  // Assign manager to selected employees

  const handleBulkPaste = async () => {
    if (!bulkCodeInput.trim()) return;
  
    const codes = bulkCodeInput
      .split(",")
      .map((c) => c.trim())
      .filter((c) => /^[0-9]+$/.test(c)); // only numbers
  
    if (codes.length === 0) return;
  
    try {
      // Call backend API to fetch employees by employeeId
      const res = await axios.post(`${apiurl}/api/employee/getbyids`, {
        employeeIds: codes,
      });
  
      const foundEmployees = res.data.employees || [];
      const foundIds = foundEmployees.map((e) => e.employeeId);
  
      const notFound = codes.filter((id) => !foundIds.includes(id));
  
      if (foundEmployees.length > 0) {
        // Remove already selected
        const newEmps = foundEmployees.filter(
          (emp) => !selectedEmployees.some((e) => e._id === emp._id)
        );
        setSelectedEmployees((prev) => [...prev, ...newEmps]);
      }
  
      if (notFound.length > 0) {
        toast.warn(`Employee IDs not found: ${notFound.join(", ")}`);
      }
  
      setBulkCodeInput(""); // clear textarea
    } catch (err) {
      console.error("Bulk paste fetch failed", err);
      toast.error("Failed to fetch employee data for pasted codes");
    }
  };
  
  const assignManager = async () => {
    if (!selectedManager || selectedEmployees.length === 0) {
      toast.error("Select manager and employees");
      return;
    }

    try {
      await axios.post(`${apiurl}/api/employee/bulk-assign-manager`, {
        employeeObjIds: selectedEmployees.map((e) => e._id),
        managerObjId: selectedManager._id,
      });

      toast.success("Manager assigned successfully!");
      onClose();
      refreshList();
    } catch (err) {
      toast.error("Failed to assign manager");
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
      <div className="bg-white p-6 w-[95%] max-w-5xl rounded-xl shadow-xl max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Bulk Assign Manager</h2>
          <button className="text-red-600 font-medium" onClick={onClose}>
            Close
          </button>
        </div>

        {/* Selected Manager */}
        {selectedManager && (
          <div className="mb-4 border p-3 rounded bg-green-50 text-sm flex justify-between items-center">
            <div>
              <strong>Selected Manager:</strong> {selectedManager.personalDetails.firstName}{" "}
              {selectedManager.personalDetails.lastName} ({selectedManager.employeeCode})
            </div>
            <button
              onClick={removeManager}
              className="text-sm text-red-600 hover:underline"
            >
              ❌ Remove
            </button>
          </div>
        )}

        {/* Manager Search */}
        <div className="mb-4 border p-3 rounded-md">
          <label className="block font-semibold mb-1">Select Manager</label>

          {managerOptions.length > 0 && (
            <>
              <input
                type="text"
                placeholder="Filter manager list..."
                value={managerInput}
                onChange={(e) => setManagerInput(e.target.value)}
                className="border px-3 py-2 mb-2 w-full rounded-md"
              />
              <div className="max-h-40 overflow-y-auto border rounded-md px-2 py-1">
                {managerOptions
                  .filter((mgr) => {
                    const fullName = `${mgr.personalDetails.firstName} ${mgr.personalDetails.lastName}`.toLowerCase();
                    const code = mgr.employeeCode?.toLowerCase();
                    const input = managerInput.toLowerCase();
                    return fullName.includes(input) || code.includes(input);
                  })
                  .map((mgr) => (
                    <div
                      key={mgr._id}
                      className="flex justify-between items-center py-1 border-b"
                    >
                      <span>
                        {mgr.personalDetails.firstName} {mgr.personalDetails.lastName} (
                        {mgr.employeeCode})
                      </span>
                      <button
                        onClick={() => setSelectedManager(mgr)}
                        className="text-sm bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                      >
                        Select
                      </button>
                    </div>
                  ))}
              </div>
            </>
          )}
        </div>

        {/* Employee Search */}
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            placeholder="Search employees..."
            value={employeeSearch}
            onChange={(e) => setEmployeeSearch(e.target.value)}
            className="border px-3 py-2 w-full rounded-md"
          />
        </div>

        {/* Employee List */}
        <div className="border rounded-md max-h-64 overflow-y-auto mb-4">
          <table className="w-full table-auto text-sm">
            <thead className="bg-gray-100 sticky top-0 text-left">
              <tr>
                <th className="p-2">Select</th>
                <th className="p-2">Code</th>
                <th className="p-2">Name</th>
                <th className="p-2">Employee ID</th>
                <th className="p-2">Current Manager</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((emp) => (
                <tr key={emp._id} className="border-b">
                  <td className="p-2 text-center">
                    <input
                      type="checkbox"
                      checked={selectedEmployees.some((e) => e._id === emp._id)}
                      onChange={() => handleCheckboxChange(emp)}
                    />
                  </td>
                  <td className="p-2">{emp.employeeCode}</td>
                  <td className="p-2 capitalize">
                    {emp.personalDetails.firstName + " " + emp.personalDetails.lastName}
                  </td>
                  <td className="p-2">{emp.employeeId}</td>
                  <td className="p-2">
                    {emp.reportingDetails?.managerName || "None"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Selected Employees */}
        {selectedEmployees.length > 0 && (
          <div className="mb-4 text-sm bg-yellow-50 border p-3 rounded">
            <strong>Selected Employees:</strong>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              {selectedEmployees.map((emp) => (
                <li key={emp._id} className="flex justify-between items-center">
                  <span>
                    {emp.personalDetails.firstName} {emp.personalDetails.lastName} -{" "}
                    {emp.employeeCode} ({emp.employeeId})
                  </span>
                  <button
                    onClick={() => removeEmployee(emp._id)}
                    className="text-red-600 text-xs hover:underline"
                  >
                    ❌ Remove
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="mb-4">
  <label className="block mb-1 font-medium text-sm">
    Paste Employee Codes (comma-separated):
  </label>
  <textarea
    rows={2}
    className="border px-3 py-2 w-full rounded-md text-sm"
    placeholder="e.g., 2894, 12, 334"
    value={bulkCodeInput}
    onChange={(e) => setBulkCodeInput(e.target.value)}
  ></textarea>
  <button
    onClick={handleBulkPaste}
    className="mt-2 bg-blue-600 text-white px-4 py-1 text-sm rounded hover:bg-blue-700"
  >
    Find & Select
  </button>
</div>

        {/* Assign Button */}
        <button
          onClick={assignManager}
          className="mt-2 bg-green-600 text-white px-5 py-2 rounded hover:bg-green-700"
        >
          Assign Selected
        </button>
      </div>
    </div>
  );
};

export default BulkAssignManager;
